import BaseApiClient from '../BaseApiClient';
import type {
  AggregatedChatResult,
  ApplyFiltersRequest,
  ApplyFiltersResponse,
  AudienceChatRequest,
  AudienceDetailResponse,
  AudienceListResponse,
  AudienceSnapshotResponse,
  DeleteAudienceResponse,
  DuplicateAudienceRequest,
  PublishAudienceResponse,
  UnmatchedAudienceRow,
  FilterOptionsRequest,
  FilterOptionsResponse,
  PropertiesResponse,
  RecentPromptsResponse,
  ExportAudienceRequest,
  PreviewRequest,
  PreviewResponse,
  SaveAudienceRequest,
  SaveAudienceResponse,
  ScvStatsResponse,
  CsvUploadResponse,
  AudiencePainPointsResponse,
  UnclassifiedTitlesRequest,
  UnclassifiedTitlesResponse
} from '@/interfaces/AudienceManifest.interface';
import { looksLikeSegmentSql, streamChatChunks } from './sse-parser';
import {
  getInsightsFetchInit,
  getInsightsApiPath,
  getInsightsUrl
} from './insights-request';

export type ChatStreamCallbacks = {
  onThought?: (text: string) => void;
  onContent?: (delta: string) => void;
};

export class ApiAudienceManifest extends BaseApiClient {
  private manifestUrl(suffix: string): string {
    const base = String(this.client.defaults.baseURL || '').replace(/\/$/, '');
    return getInsightsUrl(suffix, base);
  }

  async recentPrompts(): Promise<RecentPromptsResponse> {
    const response = await this.client.get<RecentPromptsResponse>(
      getInsightsApiPath('/api/audience/recent-prompts')
    );
    return response.data;
  }

  async chatStream(
    payload: AudienceChatRequest,
    options?: ChatStreamCallbacks
  ): Promise<AggregatedChatResult> {
    const { tenant_id, ...restPayload } = payload;
    const body: Record<string, unknown> = {
      ...restPayload,
      session_id: payload.session_id ?? null
    };
    if (tenant_id && tenant_id !== 'nexus-default') {
      body.tenant_id = tenant_id;
    }

    const response = await fetch(
      this.manifestUrl('/api/audience/chat'),
      getInsightsFetchInit(
        'POST',
        'text/event-stream',
        JSON.stringify(body)
      )
    );

    if (!response.ok) {
      if (response.status === 429) {
        throw new Error('Too many requests. Please slow down and try again.');
      }
      throw new Error('Chat request failed');
    }
    if (!response.body) {
      throw new Error('Chat response body is empty');
    }

    let contentAccumulator = '';

    const aggregated = await streamChatChunks(response.body, (chunk) => {
      if (chunk.type === 'thought' && chunk.text) {
        options?.onThought?.(chunk.text);
      }
      if (chunk.type === 'content' && chunk.text) {
        contentAccumulator += chunk.text;
        if (
          looksLikeSegmentSql(contentAccumulator) ||
          /```sql/i.test(contentAccumulator)
        ) {
          return;
        }
        options?.onContent?.(chunk.text);
      }
    });

    return {
      ...aggregated,
      sessionId: aggregated.sessionId ?? payload.session_id ?? null
    };
  }

  async properties(): Promise<PropertiesResponse> {
    const response = await this.client.get<PropertiesResponse>(
      getInsightsApiPath('/api/audience/properties')
    );
    return response.data;
  }

  async getScvStats(): Promise<ScvStatsResponse> {
    const response = await this.client.get<ScvStatsResponse>(
      getInsightsApiPath('/api/audience/scv-stats')
    );
    return response.data;
  }

  async filterOptions(
    payload: FilterOptionsRequest
  ): Promise<FilterOptionsResponse> {
    const { tenant_id, ...rest } = payload;
    const body: Record<string, unknown> = { ...rest };
    if (tenant_id && tenant_id !== 'nexus-default') {
      body.tenant_id = tenant_id;
    }
    const response = await this.client.post<FilterOptionsResponse>(
      getInsightsApiPath('/api/audience/filter-options'),
      body
    );
    return response.data;
  }

  async previewAudience(payload: PreviewRequest): Promise<PreviewResponse> {
    const { tenant_id, ...rest } = payload;
    const body: Record<string, unknown> = { ...rest };
    if (tenant_id && tenant_id !== 'nexus-default') {
      body.tenant_id = tenant_id;
    }
    const response = await this.client.post<PreviewResponse>(
      getInsightsApiPath('/api/audience/preview'),
      body
    );
    return response.data;
  }

  async getAudiencePreview(
    audienceId: string,
    params?: {
      page?: number;
      page_size?: number;
      sort_column?: string | null;
      sort_direction?: 'ASC' | 'DESC' | null;
    }
  ): Promise<PreviewResponse> {
    const searchParams = new URLSearchParams();
    if (params?.page != null) searchParams.set('page', String(params.page));
    if (params?.page_size != null) {
      searchParams.set('page_size', String(params.page_size));
    }
    if (params?.sort_column) {
      searchParams.set('sort_column', params.sort_column);
    }
    if (params?.sort_direction) {
      searchParams.set('sort_direction', params.sort_direction);
    }
    const query = searchParams.toString();
    const response = await this.client.get<PreviewResponse>(
      getInsightsApiPath(
        `/api/audience/${audienceId}/preview${query ? `?${query}` : ''}`
      )
    );
    return response.data;
  }

  async applyFilters(
    payload: ApplyFiltersRequest
  ): Promise<ApplyFiltersResponse> {
    const { tenant_id, ...rest } = payload;
    const body: Record<string, unknown> = {
      ...rest,
      session_id: payload.session_id ?? null
    };
    if (tenant_id && tenant_id !== 'nexus-default') {
      body.tenant_id = tenant_id;
    }
    const response = await this.client.post<ApplyFiltersResponse>(
      getInsightsApiPath('/api/audience/apply-filters'),
      body
    );
    return response.data;
  }

  async unclassifiedTitles(
    payload: UnclassifiedTitlesRequest
  ): Promise<UnclassifiedTitlesResponse> {
    const response = await this.client.post<UnclassifiedTitlesResponse>(
      getInsightsApiPath('/api/audience/unclassified-titles'),
      {
        filters: payload.filters,
        session_id: payload.session_id ?? null,
        limit: payload.limit ?? 50
      }
    );
    return response.data;
  }

  async listAudiences(params?: {
    page?: number;
    page_size?: number;
    status?: 'draft' | 'published';
    tenant_id?: string;
  }): Promise<AudienceListResponse> {
    const searchParams = new URLSearchParams();
    if (params?.page != null) searchParams.set('page', String(params.page));
    if (params?.page_size != null) {
      searchParams.set('page_size', String(params.page_size));
    }
    if (params?.status) searchParams.set('status', params.status);
    if (params?.tenant_id && params.tenant_id !== 'nexus-default') {
      searchParams.set('tenant_id', params.tenant_id);
    }
    const query = searchParams.toString();
    const response = await this.client.get<AudienceListResponse>(
      getInsightsApiPath(`/api/audience${query ? `?${query}` : ''}`)
    );
    return response.data;
  }

  async deleteAudience(audienceId: string): Promise<DeleteAudienceResponse> {
    const response = await this.client.delete<DeleteAudienceResponse>(
      getInsightsApiPath(`/api/audience/${audienceId}`)
    );
    return response.data;
  }

  async publishAudience(audienceId: string): Promise<PublishAudienceResponse> {
    const response = await this.client.post<PublishAudienceResponse>(
      getInsightsApiPath(`/api/audience/${audienceId}/publish`)
    );
    return response.data;
  }

  async duplicateAudience(
    audienceId: string,
    payload?: DuplicateAudienceRequest
  ): Promise<AudienceDetailResponse> {
    const response = await this.client.post<AudienceDetailResponse>(
      getInsightsApiPath(`/api/audience/${audienceId}/duplicate`),
      payload?.audience_name ? { audience_name: payload.audience_name } : {}
    );
    return response.data;
  }

  async listUnmatchedAudienceRows(
    audienceId: string
  ): Promise<UnmatchedAudienceRow[]> {
    const response = await this.client.get<
      | UnmatchedAudienceRow[]
      | { records?: UnmatchedAudienceRow[]; rows?: UnmatchedAudienceRow[] }
    >(getInsightsApiPath(`/api/audience/${audienceId}/unmatched`));
    const data = response.data;
    if (Array.isArray(data)) return data;
    if (Array.isArray(data?.records)) return data.records;
    if (Array.isArray(data?.rows)) return data.rows;
    return [];
  }

  async uploadAudienceFile(
    file: File,
    options?: { session_id?: string | null }
  ): Promise<CsvUploadResponse> {
    const formData = new FormData();
    formData.append('file', file);
    if (options?.session_id) {
      formData.append('session_id', options.session_id);
    }

    const response = await this.client.post<CsvUploadResponse>(
      getInsightsApiPath('/api/audience/upload'),
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      }
    );
    return response.data;
  }

  async saveAudience(payload: SaveAudienceRequest): Promise<SaveAudienceResponse> {
    const { tenant_id, ...rest } = payload;
    const body: Record<string, unknown> = {
      ...rest,
      status: payload.status ?? 'draft',
      audience_type: payload.audience_type ?? 'dynamic'
    };
    if (tenant_id && tenant_id !== 'nexus-default') {
      body.tenant_id = tenant_id;
    }

    const response = await this.client.post<SaveAudienceResponse>(
      getInsightsApiPath('/api/audience/save'),
      body
    );
    return response.data;
  }

  async getAudience(audienceId: string): Promise<AudienceDetailResponse> {
    const response = await this.client.get<AudienceDetailResponse>(
      getInsightsApiPath(`/api/audience/${audienceId}`)
    );
    return response.data;
  }

  async getAudiencePainPoints(
    audienceId: string
  ): Promise<AudiencePainPointsResponse> {
    const response = await this.client.get<AudiencePainPointsResponse>(
      getInsightsApiPath(`/api/audience/${audienceId}/pain-points`)
    );
    return response.data;
  }

  async updateAudience(
    audienceId: string,
    payload: SaveAudienceRequest
  ): Promise<SaveAudienceResponse> {
    const response = await this.client.patch<SaveAudienceResponse>(
      getInsightsApiPath(`/api/audience/${audienceId}`),
      payload
    );
    return response.data;
  }

  async getSnapshot(): Promise<AudienceSnapshotResponse> {
    const response = await this.client.get<AudienceSnapshotResponse>(
      getInsightsApiPath('/api/audience/snapshot')
    );
    return response.data;
  }

  async exportAudienceById(audienceId: string): Promise<Blob> {
    const response = await fetch(
      this.manifestUrl(`/api/audience/${audienceId}/export`),
      getInsightsFetchInit('GET', 'text/csv')
    );
    if (!response.ok) {
      if (response.status === 429) {
        throw new Error('Too many requests. Please slow down and try again.');
      }
      throw new Error('Export failed');
    }
    return response.blob();
  }

  async exportAudienceBySql(payload: ExportAudienceRequest): Promise<Blob> {
    const { tenant_id, ...rest } = payload;
    const body: Record<string, unknown> = { ...rest };
    if (tenant_id && tenant_id !== 'nexus-default') {
      body.tenant_id = tenant_id;
    }
    const response = await fetch(
      this.manifestUrl('/api/audience/export'),
      getInsightsFetchInit(
        'POST',
        'text/csv',
        JSON.stringify(body)
      )
    );
    if (!response.ok) {
      if (response.status === 429) {
        throw new Error('Too many requests. Please slow down and try again.');
      }
      throw new Error('Export failed');
    }
    return response.blob();
  }
}

function downloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  anchor.click();
  URL.revokeObjectURL(url);
}

export { downloadBlob };
